import { DataSet, EphemeralDataSet, DataValue, ColumnId } from '../../types';
import { FilterExpressionParser } from './FilterExpressionParser';
import { createDatasetId } from '../../types';
import logger from './logger';

/**
 * データセットにフィルタ式を適用して、フィルタ済みのEphemeralDataSetを返す
 *
 * @param dataset 元のデータセット
 * @param filterExpression フィルタ式（文字列）
 * @returns フィルタ済みのEphemeralDataSet
 * @throws フィルタ式の構文エラー、またはデータが無い場合
 */
export function applyFilterToDataset(
  dataset: DataSet,
  filterExpression: string
): EphemeralDataSet {
  logger.assert(dataset != null, 'Dataset must not be null');
  logger.assert(filterExpression != null, 'Filter expression must not be null');

  // データが無い場合はエラー
  if (!dataset.data) {
    throw new Error('Dataset does not have data');
  }

  // フィルタ式をパース
  const parser = new FilterExpressionParser();
  const parseResult = parser.parse(filterExpression);

  if (!parseResult.success) {
    throw new Error(parseResult.error || 'Invalid filter expression');
  }

  const expression = parseResult.expression!;

  // フィルタ適用
  const filteredIndices: number[] = [];
  const rowCount = dataset.rowCount || 0;

  for (let i = 0; i < rowCount; i++) {
    const rowData: Record<string, DataValue> = {};
    dataset.schema.columns.forEach(col => {
      rowData[col.name] = dataset.data![col.id][i];
    });

    if (FilterExpressionParser.evaluate(expression, rowData)) {
      filteredIndices.push(i);
    }
  }

  logger.info('[applyFilterToDataset] Filter applied', {
    originalRowCount: rowCount,
    filteredRowCount: filteredIndices.length,
    filterExpression
  });

  // フィルタ済みデータを構築
  const filteredData: Record<ColumnId, DataValue[]> = {};
  dataset.schema.columns.forEach(col => {
    const sourceData = dataset.data![col.id];
    filteredData[col.id] = filteredIndices.map(i => sourceData[i]);
  });

  // EphemeralDataSetを返す
  return {
    id: createDatasetId(`dataset-filtered-${crypto.randomUUID()}`),
    name: `${dataset.name} (filtered)`,
    type: 'ephemeral',
    schema: dataset.schema,
    rowCount: filteredIndices.length,
    data: filteredData,
    metadata: {
      createdAt: new Date().toISOString(),
      purpose: 'filtered_view',
      sourceDatasetId: dataset.id,
      filterExpression: expression
    }
  };
}
